// Theme JavaScript for Conceited Shopify Theme

// Cart functionality
class CartManager {
  constructor() {
    this.cart = {};
    this.init();
  }

  init() {
    this.updateCartCount();
    this.bindEvents();
  }

  bindEvents() {
    // Add to cart buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    const quantity = parseInt(button.dataset.quantity) || 1;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      const data = await response.json();

      if (response.ok) {
        this.updateCartCount();
        this.showNotification('Item added to cart!', 'success');
      } else {
        this.showNotification(data.description || 'Error adding item to cart', 'error');
      }
    } catch (error) {
      this.showNotification('Error adding item to cart', 'error');
    }
  }

  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.cart = cart;
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'inline' : 'none';
        });
      });
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 z-[100] px-6 py-3 text-sm font-medium shadow-lg transition-all duration-300 ${
      type === 'success' ? 'bg-primary text-primary-foreground' : 'bg-red-600 text-white'
    }`;
    notification.textContent = message;
    notification.style.transform = 'translateY(-1rem)';
    notification.style.opacity = '0';
    document.body.appendChild(notification);

    requestAnimationFrame(() => {
      notification.style.transform = 'translateY(0)';
      notification.style.opacity = '1';
    });

    setTimeout(() => {
      notification.style.transform = 'translateY(-1rem)';
      notification.style.opacity = '0';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
}

// Product gallery functionality
class ProductGallery {
  constructor(container) {
    this.container = container;
    this.mainImage = container.querySelector('[data-main-image]');
    this.thumbnails = container.querySelectorAll('[data-thumbnail]');
    this.init();
  }

  init() {
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.switchImage(index));
    });
  }

  switchImage(index) {
    const thumbnail = this.thumbnails[index];
    if (!thumbnail) return;

    const imageSrc = thumbnail.dataset.imageSrc;
    const imageAlt = thumbnail.dataset.imageAlt;

    if (this.mainImage && imageSrc) {
      this.mainImage.src = imageSrc;
      this.mainImage.alt = imageAlt || '';

      // Update active thumbnail
      this.thumbnails.forEach(t => t.classList.remove('active'));
      thumbnail.classList.add('active');
    }
  }
}

// Variant selection functionality
class VariantSelector {
  constructor(container) {
    this.container = container;
    const jsonScript = container.querySelector('[data-variant-json]');
    this.variantData = jsonScript ? JSON.parse(jsonScript.textContent) : [];
    this.priceElement = container.querySelector('[data-price]');
    this.addToCartButton = container.querySelector('[data-add-to-cart]');
    this.selectedOptions = {};
    this.init();
  }

  init() {
    const optionSelectors = this.container.querySelectorAll('[data-option-select]');
    optionSelectors.forEach(select => {
      select.addEventListener('change', (e) => {
        this.selectedOptions[select.dataset.option] = e.target.value;
        this.updateVariant();
      });
    });
  }

  updateVariant() {
    // Build selected options array from the select elements
    const optionSelectors = this.container.querySelectorAll('[data-option-select]');
    const selectedValues = [];
    optionSelectors.forEach(select => {
      selectedValues.push(select.value);
    });

    // Shopify variants use option1, option2, option3 as flat strings
    const matchingVariant = this.variantData.find(variant => {
      return selectedValues.every((value, index) => {
        return variant[`option${index + 1}`] === value;
      });
    });

    if (matchingVariant) {
      // Update price
      if (this.priceElement) {
        const formattedPrice = '$' + (matchingVariant.price / 100).toFixed(2);
        this.priceElement.textContent = formattedPrice;
      }

      // Update add to cart button
      if (this.addToCartButton) {
        this.addToCartButton.dataset.variantId = matchingVariant.id;
        this.addToCartButton.disabled = !matchingVariant.available;
        this.addToCartButton.textContent = matchingVariant.available ? 'Add to Cart' : 'Sold Out';
      }

      // Update URL without reload
      const url = new URL(window.location);
      url.searchParams.set('variant', matchingVariant.id);
      window.history.replaceState({}, '', url);
    }
  }
}

// Search functionality
class SearchManager {
  constructor() {
    this.searchInput = document.querySelector('[data-search-input]');
    this.searchResults = document.querySelector('[data-search-results]');
    this.debounceTimer = null;
    this.init();
  }

  init() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.performSearch(e.target.value);
      }, 300);
    });
  }

  async performSearch(query) {
    if (!query || query.length < 3) {
      this.hideResults();
      return;
    }

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
      const data = await response.json();

      this.displayResults(data.resources.results.products);
    } catch (error) {
      console.error('Search error:', error);
      this.hideResults();
    }
  }

  displayResults(products) {
    if (!this.searchResults) return;

    const html = products.map(product => `
      <a href="${product.url}" class="block p-3 hover:bg-gray-50">
        <div class="flex items-center space-x-3">
          <img src="${product.image}" alt="${product.title}" class="w-12 h-12 object-cover">
          <div>
            <div class="font-medium">${product.title}</div>
            <div class="text-sm text-gray-500">${Shopify.formatMoney(product.price)}</div>
          </div>
        </div>
      </a>
    `).join('');

    this.searchResults.innerHTML = html;
    this.searchResults.classList.remove('hidden');
  }

  hideResults() {
    if (this.searchResults) {
      this.searchResults.classList.add('hidden');
    }
  }
}

// Initialize everything when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize cart manager
  new CartManager();

  // Initialize product galleries
  document.querySelectorAll('[data-product-gallery]').forEach(container => {
    new ProductGallery(container);
  });

  // Initialize variant selectors
  document.querySelectorAll('[data-variant-selector]').forEach(container => {
    new VariantSelector(container);
  });

  // Initialize search
  new SearchManager();

  // Wishlist functionality (localStorage based)
  // Stores: { variantId, productHandle, productTitle, colorName, imageUrl, imageAlt, price, currencyCode }
  window.WishlistManager = {
    items: JSON.parse(localStorage.getItem('conceited-wishlist') || '[]'),

    toggleItem(variantId, productData) {
      const index = this.items.findIndex(item => item.variantId === String(variantId));

      if (index > -1) {
        this.items.splice(index, 1);
      } else {
        // Ensure productData includes productHandle for wishlist page
        const data = typeof productData === 'string' ? JSON.parse(productData) : productData;
        data.variantId = String(variantId);
        this.items.push(data);
      }

      localStorage.setItem('conceited-wishlist', JSON.stringify(this.items));
      this.updateUI();
    },

    isInWishlist(variantId) {
      return this.items.some(item => item.variantId === String(variantId));
    },

    updateUI() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
        const variantId = btn.dataset.variantId;
        const isActive = this.isInWishlist(variantId);
        const heart = btn.querySelector('.wishlist-heart');

        // Toggle aria-label
        btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');

        // Toggle heart fill state (matches React: fill-foreground when active)
        if (heart) {
          if (isActive) {
            heart.setAttribute('fill', 'currentColor');
            heart.classList.add('text-foreground');
          } else {
            heart.setAttribute('fill', 'none');
            heart.classList.remove('text-foreground');
          }
        }

        // Also update text-based wishlist buttons (e.g., on product page)
        const wishlistText = btn.querySelector('.wishlist-text');
        if (wishlistText) {
          wishlistText.textContent = isActive ? 'Remove from Wishlist' : 'Add to Wishlist';
        }

        btn.classList.toggle('active', isActive);
      });

      // Update navbar wishlist heart (heartbeat animation on change, always stroke)
      const navHeart = document.querySelector('[data-nav-wishlist-heart]');
      if (navHeart && this.items.length > 0) {
        navHeart.classList.add('heartbeat');
        setTimeout(() => navHeart.classList.remove('heartbeat'), 600);
      }
    }
  };

  // Bind wishlist toggle buttons (capture phase so it fires BEFORE <a> navigation)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (btn) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      const variantId = btn.dataset.variantId;
      const productData = btn.dataset.productData ? JSON.parse(btn.dataset.productData) : {};
      window.WishlistManager.toggleItem(variantId, productData);

      // Brief scale animation
      btn.style.transform = 'scale(1.25)';
      setTimeout(() => { btn.style.transform = ''; }, 300);
    }
  }, true); // true = capture phase

  // Update wishlist UI on load
  window.WishlistManager.updateUI();
});